/**
 * LDB Duel — Custom Metal Field Center Designer
 * Procedural 3D gold-card preview + artwork placement tool.
 *
 * This is the card-sleeve designer's build, layout and functionality applied
 * to the metal field center (2026-09-29, forked from sleeve-designer.js at
 * d20043dce6dfbc7d6cde756337958c97). Removed relative to the sleeve tool:
 * the Card Size and Finish Style variant plumbing (the field center is one
 * fixed 45.9x66.3mm card in one gold finish) and the quantity/unit-price
 * handling. Kept from the previous 2D field center tool: the gold base +
 * markings overlay texture pipeline, the markings-colour swatches, the
 * capture/order conventions and the template's setting IDs.
 *
 * Architecture notes for future maintainers:
 *  - three.js is loaded as a CLASSIC script exposing window.THREE. A dynamic
 *    import() failed in production ("require is not defined") because an app
 *    bundler runtime on the storefront patches dynamic import. The section
 *    also emits a deferred <script> for the global build, so window.THREE
 *    normally already exists.
 *  - The card is generated in code, not loaded from a .glb: one gold metal
 *    slab with the printed front on one face and the fixed embossed gold
 *    back on the other.
 *  - The product form is re-rendered by product-info.js on every variant
 *    change (it replaces the innerHTML of #ProductBuyButtons). Any input we
 *    inject is therefore destroyed on each variant switch and must be
 *    re-injected. See ensureFormInputs().
 */
(function () {
  'use strict';

  var MM_PER_INCH = 25.4;
  var LOW_DPI_THRESHOLD = 300;  // metal print: aim for 300+
  var STACK_BREAKPOINT = 1199;  // must match the media query in the CSS

  function boot() {
    var roots = document.querySelectorAll('[data-fieldcenter-designer]');
    for (var i = 0; i < roots.length; i++) {
      // shopify:section:load fires on EVERY setting change in the theme editor.
      // Without this guard each firing built another instance on the same root,
      // each with its own listeners and its own 3D renderer.
      if (roots[i].__fcInit) continue;
      roots[i].__fcInit = true;
      try {
        new FieldcenterDesigner(roots[i]);
      } catch (err) {
        roots[i].__fcInit = false;
        console.error('[fieldcenter-designer] init failed', err);
      }
    }
  }

  // ---------------------------------------------------------------- helpers

  function parseJSONAttr(el, name, fallback) {
    var raw = el.getAttribute(name);
    if (!raw) return fallback;
    try { return JSON.parse(raw); } catch (e) {
      console.warn('[fieldcenter-designer] bad JSON in ' + name, e);
      return fallback;
    }
  }

  function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

  function hasWebGL() {
    try {
      var c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext &&
        (c.getContext('webgl2') || c.getContext('webgl')));
    } catch (e) { return false; }
  }

  function roundedRectPath(ctx, x, y, w, h, r) {
    r = Math.min(r, w / 2, h / 2);
    // Square corners: draw an actual rectangle rather than degenerate curves.
    if (!(r > 0.5)) {
      ctx.beginPath();
      ctx.rect(x, y, w, h);
      ctx.closePath();
      return;
    }
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  function bytesToLabel(b) {
    if (b < 1024) return b + ' B';
    if (b < 1024 * 1024) return Math.round(b / 1024) + ' KB';
    return (b / (1024 * 1024)).toFixed(1) + ' MB';
  }

  // Scale factor mapping image pixels -> canvas pixels such that the rotated
  // image fully covers the canvas.
  function coverFactor(iw, ih, cw, ch, rot) {
    var a = Math.abs(Math.cos(rot));
    var b = Math.abs(Math.sin(rot));
    var bboxW = iw * a + ih * b;
    var bboxH = iw * b + ih * a;
    return Math.max(cw / bboxW, ch / bboxH);
  }

  // ------------------------------------------------------------ constructor

  function FieldcenterDesigner(root) {
    this.root = root;
    root.__fc = this;

    // The printed card, in mm. 45.9 x 66.3 is the standard Yu-Gi-Oh! field
    // center; full bleed, so the artwork runs edge to edge.
    this.cardW = parseFloat(root.getAttribute('data-card-w-mm'));
    if (isNaN(this.cardW)) this.cardW = 45.9;
    this.cardH = parseFloat(root.getAttribute('data-card-h-mm'));
    if (isNaN(this.cardH)) this.cardH = 66.3;
    this.cornerMM = parseFloat(root.getAttribute('data-corner-mm'));
    if (isNaN(this.cornerMM)) this.cornerMM = 2;

    // Real metal card construction, in mm. thicknessScale lets the edge be
    // exaggerated if a more legible mockup is wanted.
    this.thicknessMM = parseFloat(root.getAttribute('data-thickness-mm'));
    if (isNaN(this.thicknessMM)) this.thicknessMM = 0.6;
    this.thicknessScale = parseFloat(root.getAttribute('data-thickness-scale')) || 1;

    // Capture resolution: matches the overlay assets' native pixels
    // (1300 x 1878 = 28.3 px/mm) so the markings render 1:1, never resampled.
    this.capW = parseInt(root.getAttribute('data-capture-w'), 10) || 1300;
    this.capH = Math.round(this.capW * this.cardH / this.cardW);

    this.baseUrl = root.getAttribute('data-base-url') || '';
    this.backUrl = root.getAttribute('data-back-url') || '';
    this.overlayUrls = {
      white: root.getAttribute('data-overlay-white-url') || '',
      black: root.getAttribute('data-overlay-black-url') || ''
    };
    this.images = {};   // key -> HTMLImageElement (lazy, re-render on load)

    this.threeUrl = root.getAttribute('data-three-url');
    this.maxBytes = (parseFloat(root.getAttribute('data-max-mb')) || 15) * 1024 * 1024;
    this.markingsOptionName = root.getAttribute('data-markings-option') || 'Field Center Design';
    this.requireArtwork = root.getAttribute('data-require-artwork') === 'true';
    this.shortenLabels = root.getAttribute('data-short-labels') !== 'false';
    this.hideOriginal = root.getAttribute('data-hide-original') === 'true';
    this.adoptBuy = root.getAttribute('data-adopt-buy') === 'true';
    this.moveQuantity = root.getAttribute('data-move-qty') === 'true';
    this.collapseHeading = root.getAttribute('data-collapse-heading') || '';
    this.collapseOpen = root.getAttribute('data-collapse-open') === 'true';
    this.movePaymentTerms = root.getAttribute('data-move-terms') === 'true';
    this.moneySample = root.getAttribute('data-money-sample') || '$1.00';
    this.markColorLabel = root.getAttribute('data-mark-color-label') || 'Markings colour';

    // artwork colour boost, applied to the composited preview only; the
    // customer's file goes to the order untouched. Metal print is vivid on
    // its own, so the default is neutral. artBright feeds only the 3D
    // texture copy (see updateFxCanvas), like the sleeve tool's.
    this.artSat = parseFloat(root.getAttribute('data-art-sat'));
    if (isNaN(this.artSat)) this.artSat = 1.0;
    this.artBright = parseFloat(root.getAttribute('data-art-bright'));
    if (isNaN(this.artBright)) this.artBright = 1.0;

    this.variants = parseJSONAttr(root, 'data-variants', null);
    if (!this.variants) {
      var vnode = root.querySelector('[data-fc-variants-json]');
      if (vnode) { try { this.variants = JSON.parse(vnode.textContent); } catch (e) { this.variants = []; } }
    }
    this.variants = this.variants || [];

    this.state = {
      img: null,
      file: null,
      tx: 0,          // mm offset from card centre
      ty: 0,
      scale: 1,
      rot: 0,         // radians
      markValue: null,       // the real option value, read from the picker
      markColor: 'white',    // 'white' | 'black' | a '#rrggbb' custom pick
      customColors: []       // picked colours, shown as their own swatches (max 5)
    };

    this.pointers = {};
    this.dragging = false;

    this.cacheDom();
    this.buildTextureCanvas();
    this.bindUI();
    this.syncFromVariantPicker();
    this.watchVariant();
    this.render2D();

    if (hasWebGL() && this.threeUrl) {
      this.init3D();
    } else if (!this.threeUrl) {
      this.fail3D('three.global.min.js is not in the theme assets folder (asset_url resolved to nothing).');
    } else {
      this.fail3D('WebGL is unavailable in this browser.');
    }

    // Core first, and each optional enhancement isolated. A throw in any one
    // of these must never take out selection, pricing or the cart.
    var self = this;
    function step(name, fn) {
      try {
        fn.call(self);
      } catch (err) {
        console.error('[fieldcenter-designer] ' + name + ' failed (continuing)', err);
      }
    }

    step('syncTabs', this.syncTabs);
    step('hideOriginalPicker', this.hideOriginalPicker);
    step('adoptBuyButtons', this.adoptBuyButtons);
    step('adoptQuantity', this.adoptQuantity);

    // core: these must run even if anything above misbehaved
    step('buildOptionProxy', this.buildOptionProxy);
    step('updatePrice', this.updatePrice);
    step('ensureFormInputs', this.ensureFormInputs);
    step('wireAtc', this.wireAtc);
    step('updateGate', this.updateGate);
    step('bindBuyGuard', this.bindBuyGuard);
    step('alignPriceToButtons', this.alignPriceToButtons);
    var self2 = this;
    setTimeout(function () { self2.alignPriceToButtons(); }, 800);
    setTimeout(function () { self2.alignPriceToButtons(); }, 2500);

    // purely presentational, deliberately last
    step('collapseDescriptionSection', this.collapseDescriptionSection);
    step('adoptPaymentTerms', this.adoptPaymentTerms);
  }

  FieldcenterDesigner.prototype.cacheDom = function () {
    var r = this.root;
    var q = function (sel) { return r.querySelector(sel); };
    this.el = {
      stage3d: q('[data-fc-stage]'),
      view: q('[data-fc-view]'),
      fileInput: q('[data-fc-file]'),
      dropzone: q('[data-fc-dropzone]'),
      zoom: q('[data-fc-zoom]'),
      rotate: q('[data-fc-rotate]'),
      reset: q('[data-fc-reset]'),
      remove: q('[data-fc-remove]'),
      status: q('[data-fc-status]'),
      dpi: q('[data-fc-dpi]'),
      filename: q('[data-fc-filename]'),
      controls: q('[data-fc-controls]'),
      tabs: r.querySelectorAll('[data-fc-tab]'),
      panes: r.querySelectorAll('[data-fc-pane]'),
      spinBtn: q('[data-fc-spin]'),
      options: q('[data-fc-options="primary"]') || q('[data-fc-options]'),
      markRow: q('[data-fc-mark-colors]'),
      priceNow: q('[data-fc-price-now]'),
      priceWas: q('[data-fc-price-was]'),
      atc: q('[data-fc-atc]'),
      buySlot: q('[data-fc-buy-slot]'),
      qtySlot: q('[data-fc-qty-slot]'),
      canvasWrap: q('.fc-canvas-wrap')
    };
  };

  // ------------------------------------------------------------ images

  /** Lazy image loader: base, back and overlays re-render when ready. */
  FieldcenterDesigner.prototype.image = function (key, url) {
    if (!url) return null;
    var cached = this.images[key];
    if (cached) return cached.complete && cached.naturalWidth ? cached : null;
    var self = this;
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      self.render2D();
      if (key === 'back') self.updateBackTexture();
    };
    img.src = url;
    this.images[key] = img;
    return null;
  };

  FieldcenterDesigner.prototype.baseImage = function () {
    return this.image('base', this.baseUrl);
  };

  FieldcenterDesigner.prototype.backImage = function () {
    return this.image('back', this.backUrl);
  };

  FieldcenterDesigner.prototype.overlayImage = function () {
    if (!this.markingsOn()) return null;
    if (this.state.markColor.charAt(0) === '#') return this.tintedOverlay();
    var c = this.state.markColor === 'black' ? 'black' : 'white';
    return this.image('overlay-' + c, this.overlayUrls[c]);
  };

  /**
   * Any-colour markings (2026-09-29, Mert): the white overlay is used as an
   * alpha MASK and refilled with the picked colour — source-in keeps the
   * mask's alpha, anti-aliased edges included, so the markings stay as
   * crisp as the shipped white/black PNGs at every colour. Cached per
   * colour; drawImage() accepts the canvas exactly like an image.
   */
  FieldcenterDesigner.prototype.tintedOverlay = function () {
    var mask = this.image('overlay-white', this.overlayUrls.white);
    if (!mask) return null;
    var col = this.state.markColor;
    if (this._tintCache && this._tintCache.color === col) return this._tintCache.canvas;
    var c = document.createElement('canvas');
    c.width = mask.naturalWidth;
    c.height = mask.naturalHeight;
    var ctx = c.getContext('2d');
    ctx.drawImage(mask, 0, 0);
    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = col;
    ctx.fillRect(0, 0, c.width, c.height);
    this._tintCache = { color: col, canvas: c };
    return c;
  };

  /** Markings print only when the "Add ..." variant is selected. Matched by
   *  keyword so admin rewording keeps working: any value containing the word
   *  "no" (as in "No Field Center Markings") turns them off. */
  FieldcenterDesigner.prototype.markingsOn = function () {
    var v = this.state.markValue;
    if (v == null) return true;   // option missing entirely: show the markings
    return !/\bno\b/i.test(String(v));
  };

  // ------------------------------------------------------------- 2D texture

  FieldcenterDesigner.prototype.buildTextureCanvas = function () {
    // The full print composition at capture scale. Everything the customer
    // sees — the 2D editor, the 3D card face and the order's Design Preview —
    // renders from this one canvas, so they can never disagree.
    this.texCanvas = document.createElement('canvas');
    this.texCtx = this.texCanvas.getContext('2d');
    // The 3D print renders from a filtered COPY, so any saturation/brightness
    // boost applies to the preview without altering the 2D editor, the
    // customer's file, or what the designer receives.
    this.fxCanvas = document.createElement('canvas');
    this.fxCtx = this.fxCanvas.getContext('2d');
  };

  FieldcenterDesigner.prototype.updateFxCanvas = function () {
    var src = this.texCanvas, dst = this.fxCanvas, ctx = this.fxCtx;
    if (dst.width !== src.width || dst.height !== src.height) {
      dst.width = src.width;
      dst.height = src.height;
      // The GPU texture was allocated at the OLD canvas size. three.js uploads
      // resized canvases with texSubImage2D into that stale allocation, which
      // squeezes the print into a corner and leaves stale pixels along two
      // edges. dispose() drops the allocation so the next upload re-creates
      // the texture at the new dimensions.
      if (this.tex) this.tex.dispose();
    }
    ctx.clearRect(0, 0, dst.width, dst.height);
    var f = 'saturate(' + this.artSat + ') brightness(' + this.artBright + ')';
    try { ctx.filter = f; } catch (e) { /* very old browsers: unfiltered copy */ }
    ctx.drawImage(src, 0, 0);
    ctx.filter = 'none';
  };

  /** Gold base -> artwork (transformed) -> markings overlay, at print scale. */
  FieldcenterDesigner.prototype.drawTexture = function () {
    var cw = this.capW, ch = this.capH;
    if (this.texCanvas.width !== cw) this.texCanvas.width = cw;
    if (this.texCanvas.height !== ch) this.texCanvas.height = ch;

    var ctx = this.texCtx;
    ctx.clearRect(0, 0, cw, ch);

    // gold metal base — flat fallback colour until the texture loads
    var base = this.baseImage();
    if (base) {
      ctx.drawImage(base, 0, 0, cw, ch);
    } else {
      ctx.fillStyle = '#d9b559';
      ctx.fillRect(0, 0, cw, ch);
    }

    // artwork
    var st = this.state;
    var pxPerMM = cw / this.cardW;
    if (st.img) {
      var iw = st.img.naturalWidth || st.img.width;
      var ih = st.img.naturalHeight || st.img.height;
      var k = coverFactor(iw, ih, cw, ch, st.rot) * st.scale;
      ctx.save();
      if (ctx.filter !== undefined && this.artSat !== 1) {
        ctx.filter = 'saturate(' + this.artSat + ')';
      }
      ctx.imageSmoothingQuality = 'high';
      ctx.translate(cw / 2 + st.tx * pxPerMM, ch / 2 + st.ty * pxPerMM);
      ctx.rotate(st.rot);
      ctx.drawImage(st.img, -iw * k / 2, -ih * k / 2, iw * k, ih * k);
      ctx.restore();
      this.lastCover = k;
    } else {
      this.lastCover = null;
    }

    // field-center markings, drawn last so they sit over the artwork —
    // exactly as printed
    var ov = this.overlayImage();
    if (ov) ctx.drawImage(ov, 0, 0, cw, ch);

    if (this.tex) {
      this.updateFxCanvas();
      this.tex.needsUpdate = true;
      // The render loop only draws when asked. Without this, a texture change
      // with auto-rotate stopped leaves the canvas showing the LAST frame --
      // stale artwork included.
      this.needsRender = true;
    }
  };

  /** Visible editor canvas: the texture, clipped to the card outline, plus guides. */
  FieldcenterDesigner.prototype.render2D = function () {
    this.drawTexture();

    var view = this.el.view;
    if (!view) return;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var boxW = view.clientWidth || 240;
    var boxH = Math.round(boxW * (this.cardH / this.cardW));

    if (view.width !== Math.round(boxW * dpr) || view.height !== Math.round(boxH * dpr)) {
      view.width = Math.round(boxW * dpr);
      view.height = Math.round(boxH * dpr);
    }
    view.style.height = boxH + 'px';

    var ctx = view.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, boxW, boxH);

    var pxPerMM = boxW / this.cardW;
    var radius = this.cornerMM * pxPerMM;

    ctx.save();
    roundedRectPath(ctx, 0, 0, boxW, boxH, radius);
    ctx.clip();
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(this.texCanvas, 0, 0, boxW, boxH);
    ctx.restore();

    // card outline guide
    ctx.save();
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(255,255,255,0.55)';
    roundedRectPath(ctx, 0.5, 0.5, boxW - 1, boxH - 1, radius);
    ctx.stroke();
    ctx.restore();

    this.updateReadouts();
    this.updateNote();      // synchronous with every state change
    this.scheduleShot();
  };

  /**
   * Placement snapshot for the order: the composited card at full print
   * resolution (the overlay assets' native 1300x1878, screen-independent)
   * with the card outline guide drawn over it — gold base, artwork and
   * markings exactly as they print. Attached as properties[Design Preview]
   * beside the original file; this is how the print team verifies the
   * customer's placement against the print-ready file. The cart thumbnail
   * patches prefer a "Design Preview" property, so cart lines pick it up too.
   *
   * Captured eagerly on a 600ms debounce after each change. If capture ever
   * fails the order still goes through with the artwork alone — a submit
   * path must never wait on an async artifact.
   */
  FieldcenterDesigner.prototype.scheduleShot = function () {
    if (!this.state.img) return;
    var self = this;
    clearTimeout(this._shotTimer);
    this._shotTimer = setTimeout(function () { self.captureShot(); }, 600);
  };

  FieldcenterDesigner.prototype.captureShot = function () {
    var self = this;
    if (!this.state.img || !this.texCanvas || !this.texCanvas.width) return;
    try {
      var c = document.createElement('canvas');
      if (!c.toBlob) return;
      var boxW = this.texCanvas.width, boxH = this.texCanvas.height;
      c.width = boxW;
      c.height = boxH;
      var ctx = c.getContext('2d');
      var pxPerMM = boxW / this.cardW;
      var radius = this.cornerMM * pxPerMM;
      var lw = Math.max(2, boxW / 800);   // guide weight, visible at print scale

      ctx.save();
      roundedRectPath(ctx, 0, 0, boxW, boxH, radius);
      ctx.clip();
      ctx.drawImage(this.texCanvas, 0, 0);
      ctx.restore();

      ctx.save();
      ctx.lineWidth = lw;
      ctx.strokeStyle = 'rgba(255,255,255,0.55)';
      roundedRectPath(ctx, lw / 2, lw / 2, boxW - lw, boxH - lw, radius);
      ctx.stroke();
      ctx.restore();

      c.toBlob(function (blob) {
        if (!blob || !self.state.img) return;
        self.shotFile = new File([blob], 'placement-preview.jpg', { type: 'image/jpeg' });
        var form = self.form || self.findForm();
        var input = form && form.querySelector('[data-fc-prop="shot"]');
        if (input) self.setFileOnInput(input, self.shotFile);
      }, 'image/jpeg', 0.9);
    } catch (e) { /* best-effort only */ }
  };

  /** Keeps properties[_design] in step with the current state, every frame. */
  FieldcenterDesigner.prototype.updateNote = function () {
    var form = this.form || this.findForm();
    if (!form) return;
    var note = form.querySelector('[data-fc-prop="note"]');
    if (!note) return;
    note.value = this.state.file ? this.designNote() : '';
  };

  FieldcenterDesigner.prototype.updateReadouts = function () {
    if (!this.el.dpi) return;
    if (this.lastCover) {
      var pxPerMM = this.capW / this.cardW;
      var ppi = (MM_PER_INCH * pxPerMM) / this.lastCover;
      var low = ppi < LOW_DPI_THRESHOLD;
      this.el.dpi.textContent = Math.round(ppi) + ' PPI at this size' +
        (low ? ' — aim for ' + LOW_DPI_THRESHOLD + '+ for a crisp metal print' : '');
      this.el.dpi.classList.toggle('fc-warn', low);
      this.el.dpi.hidden = false;
    } else {
      this.el.dpi.hidden = true;
    }
  };

  // ---------------------------------------------------------------- upload

  FieldcenterDesigner.prototype.loadFile = function (file) {
    var self = this;
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      return this.say('That file is not an image. Please upload a PNG or JPG.', true);
    }
    if (file.size > this.maxBytes) {
      return this.say('That file is ' + bytesToLabel(file.size) + '. Please keep it under ' +
        bytesToLabel(this.maxBytes) + '.', true);
    }

    var url = URL.createObjectURL(file);
    var img = new Image();
    img.onload = function () {
      // Downscale oversized images for smooth canvas work. The PPI meter is
      // corrected by srcScale so it reports the ORIGINAL file's resolution.
      // The customer's original file is what goes to the cart, untouched.
      var MAXDIM = 2600;
      var big = Math.max(img.naturalWidth, img.naturalHeight);
      if (big > MAXDIM) {
        var r = MAXDIM / big;
        var c = document.createElement('canvas');
        c.width = Math.round(img.naturalWidth * r);
        c.height = Math.round(img.naturalHeight * r);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        var small = new Image();
        small.onload = function () {
          URL.revokeObjectURL(url);
          self.srcScale = 1 / r;
          self.adopt(small, file);
        };
        small.src = c.toDataURL('image/jpeg', 0.92);
      } else {
        self.srcScale = 1;
        self.adopt(img, file);
      }
    };
    img.onerror = function () {
      URL.revokeObjectURL(url);
      self.say('That image could not be read. Try re-saving it as a PNG or JPG.', true);
    };
    img.src = url;
  };

  FieldcenterDesigner.prototype.adopt = function (img, file) {
    this.state.img = img;
    this.state.file = file;
    this.state.tx = 0; this.state.ty = 0; this.state.scale = 1; this.state.rot = 0;
    if (this.el.zoom) this.el.zoom.value = '100';
    if (this.el.rotate) this.el.rotate.value = '0';
    if (this.el.filename) this.el.filename.textContent = file.name + ' — ' + bytesToLabel(file.size);
    this.root.classList.add('fc--has-art');
    this.say('');

    // Auto-rotate kicks in once artwork exists, so the customer sees the
    // card as an object without having to discover dragging. Any pointer
    // interaction with the 3D stops it (bindOrbit). Respects reduced-motion.
    if (this.renderer &&
        !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
      this.spin = true;
      if (this.el.spinBtn) this.el.spinBtn.setAttribute('aria-pressed', 'true');
    }

    this.render2D();
    // (constructor runs the full step sequence; here only the form wiring
    // and gate need refreshing)
    this.ensureFormInputs();
    this.updateGate();
    this.updatePrice();
  };

  FieldcenterDesigner.prototype.clearArt = function () {
    this.state.img = null;
    this.state.file = null;
    this.shotFile = null;
    clearTimeout(this._shotTimer);
    this.srcScale = 1;
    this.state.rot = 0; this.state.scale = 1; this.state.tx = 0; this.state.ty = 0;
    if (this.el.zoom) this.el.zoom.value = '100';
    if (this.el.rotate) this.el.rotate.value = '0';
    this.root.classList.remove('fc--has-art');
    if (this.el.filename) this.el.filename.textContent = '';
    if (this.el.fileInput) this.el.fileInput.value = '';
    this.render2D();
    this.ensureFormInputs();
    this.updateGate();
    this.updatePrice();
  };

  FieldcenterDesigner.prototype.say = function (msg, isError) {
    if (!this.el.status) return;
    this.el.status.textContent = msg || '';
    this.el.status.classList.toggle('fc-warn', !!isError);
    this.el.status.hidden = !msg;
  };

  // ------------------------------------------------------------ interaction

  FieldcenterDesigner.prototype.bindUI = function () {
    var self = this;
    var el = this.el;

    if (el.fileInput) {
      el.fileInput.addEventListener('change', function () {
        self.loadFile(this.files && this.files[0]);
      });
    }

    if (el.dropzone) {
      ['dragenter', 'dragover'].forEach(function (evt) {
        el.dropzone.addEventListener(evt, function (e) {
          e.preventDefault(); el.dropzone.classList.add('fc-drop-hot');
        });
      });
      ['dragleave', 'drop'].forEach(function (evt) {
        el.dropzone.addEventListener(evt, function (e) {
          e.preventDefault(); el.dropzone.classList.remove('fc-drop-hot');
        });
      });
      el.dropzone.addEventListener('drop', function (e) {
        if (e.dataTransfer && e.dataTransfer.files) self.loadFile(e.dataTransfer.files[0]);
      });
    }

    if (el.zoom) {
      el.zoom.addEventListener('input', function () {
        self.state.scale = clamp(parseFloat(this.value) / 100, 0.25, 4);
        self.render2D();
      });
    }
    if (el.rotate) {
      el.rotate.addEventListener('input', function () {
        self.state.rot = parseFloat(this.value) * Math.PI / 180;
        self.render2D();
      });
    }
    if (el.reset) {
      el.reset.addEventListener('click', function () {
        self.state.tx = 0; self.state.ty = 0; self.state.scale = 1; self.state.rot = 0;
        if (el.zoom) el.zoom.value = '100';
        if (el.rotate) el.rotate.value = '0';
        self.render2D();
      });
    }
    if (el.remove) {
      el.remove.addEventListener('click', function () { self.clearArt(); });
    }

    // drag to reposition on the 2D editor, pinch/wheel to zoom
    if (el.view) {
      var last = null;
      el.view.addEventListener('pointerdown', function (e) {
        if (!self.state.img) return;
        el.view.setPointerCapture(e.pointerId);
        self.pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
        last = { x: e.clientX, y: e.clientY };
        self.dragging = true;
      });
      el.view.addEventListener('pointermove', function (e) {
        if (!self.dragging || !self.state.img) return;
        self.pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
        var ids = Object.keys(self.pointers);
        if (ids.length >= 2) {
          // pinch zoom
          var a = self.pointers[ids[0]], b = self.pointers[ids[1]];
          var d = Math.hypot(a.x - b.x, a.y - b.y);
          if (self.pinchBase) {
            var next = clamp(self.pinchScale * (d / self.pinchBase), 0.25, 4);
            self.state.scale = next;
            if (el.zoom) el.zoom.value = String(Math.round(next * 100));
          } else {
            self.pinchBase = d; self.pinchScale = self.state.scale;
          }
          self.render2D();
          return;
        }
        var mmPerPx = self.cardW / (el.view.clientWidth || 240);
        self.state.tx = clamp(self.state.tx + (e.clientX - last.x) * mmPerPx, -self.cardW, self.cardW);
        self.state.ty = clamp(self.state.ty + (e.clientY - last.y) * mmPerPx, -self.cardH, self.cardH);
        last = { x: e.clientX, y: e.clientY };
        self.render2D();
      });
      ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (evt) {
        el.view.addEventListener(evt, function (e) {
          delete self.pointers[e.pointerId];
          if (!Object.keys(self.pointers).length) {
            self.dragging = false; self.pinchBase = null;
          }
        });
      });
      el.view.addEventListener('wheel', function (e) {
        if (!self.state.img) return;
        e.preventDefault();
        var next = clamp(self.state.scale * (e.deltaY < 0 ? 1.06 : 0.94), 0.25, 4);
        self.state.scale = next;
        if (el.zoom) el.zoom.value = String(Math.round(next * 100));
        self.render2D();
      }, { passive: false });
      el.view.addEventListener('dblclick', function () {
        if (!self.state.img) return;
        self.state.tx = 0; self.state.ty = 0; self.state.scale = 1; self.state.rot = 0;
        if (el.zoom) el.zoom.value = '100';
        if (el.rotate) el.rotate.value = '0';
        self.render2D();
      });
    }

    // tabs (stacked layout)
    for (var i = 0; i < el.tabs.length; i++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          self.setTab(btn.getAttribute('data-fc-tab'));
        });
      })(el.tabs[i]);
    }

    if (el.spinBtn) {
      el.spinBtn.addEventListener('click', function () { self.toggleSpin(); });
    }

    var ro = window.ResizeObserver ? new ResizeObserver(function () {
      self.syncTabs();
      self.render2D();
      self.resize3D();
    }) : null;
    if (ro) ro.observe(this.root);
    window.addEventListener('resize', function () { self.syncTabs(); self.alignPriceToButtons(); });
  };

  FieldcenterDesigner.prototype.setTab = function (name) {
    var el = this.el, i;
    this.currentTab = name;
    for (i = 0; i < el.tabs.length; i++) {
      var t = el.tabs[i];
      var on = t.getAttribute('data-fc-tab') === name;
      t.classList.toggle('fc-tab--on', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    }
    for (i = 0; i < el.panes.length; i++) {
      var p = el.panes[i];
      p.hidden = p.getAttribute('data-fc-pane') !== name;
    }
    if (name === 'preview') { this.resize3D(); }
    else { this.render2D(); }
  };

  /**
   * Keeps pane visibility in step with the CSS breakpoint.
   *
   * Below STACK_BREAKPOINT the panes stack and the tabs take over, so exactly
   * one pane must be visible. Without this the tabs rendered but nothing was
   * hidden until the customer clicked one -- both panes stacked and the
   * section ran far too tall, defeating the no-scroll layout.
   */
  FieldcenterDesigner.prototype.syncTabs = function () {
    var stacked = window.matchMedia
      ? window.matchMedia('(max-width: ' + STACK_BREAKPOINT + 'px)').matches
      : window.innerWidth <= STACK_BREAKPOINT;

    if (stacked) {
      if (!this.stacked) {
        this.stacked = true;
        this.setTab(this.currentTab || 'design');
      }
    } else if (this.stacked !== false) {
      this.stacked = false;
      for (var i = 0; i < this.el.panes.length; i++) this.el.panes[i].hidden = false;
      this.render2D();
      this.resize3D();
    }
  };

  // ------------------------------------------------------------------- 3D

  /**
   * Loads three.js as a CLASSIC script exposing window.THREE.
   *
   * This was a dynamic import() of the ESM build on the sleeve tool and it
   * FAILED IN PRODUCTION with "require is not defined" -- while importing
   * fine from the console on the same page. three.js itself never calls
   * require(); the only matches in the file are inside strings. Something
   * else on the storefront (an app bundler runtime -- LayoutHub, Afterpay and
   * others are present) patches dynamic import(). A plain <script> tag has
   * none of that surface and cannot be intercepted.
   *
   * The section also emits a deferred <script> for the global build, so
   * window.THREE is normally already there and this never has to fetch.
   */
  function loadThree(url, cb) {
    if (window.THREE) return cb(window.THREE);

    var existing = document.querySelector('script[data-fc-three], script[data-sd-three]');
    if (existing) {
      existing.addEventListener('load', function () { cb(window.THREE || null, 'script loaded but no global'); });
      existing.addEventListener('error', function () { cb(null, 'script tag failed'); });
      return;
    }
    if (!url) return cb(null, 'empty asset url');

    var sc = document.createElement('script');
    sc.src = url;
    sc.async = true;
    sc.setAttribute('data-fc-three', '');
    sc.onload = function () { cb(window.THREE || null, window.THREE ? null : 'script loaded but no global'); };
    sc.onerror = function () {
      // last resort: some environments block injected scripts but allow modules
      try {
        import(url).then(function (m) { cb(m || null, null); })
                   .catch(function (e) { cb(null, 'script tag AND import failed: ' + (e && e.message)); });
      } catch (e) {
        cb(null, 'script tag failed and import unavailable');
      }
    };
    document.head.appendChild(sc);
  }

  FieldcenterDesigner.prototype.init3D = function () {
    var self = this;
    loadThree(this.threeUrl, function (THREE, why) {
      if (!THREE) {
        return self.fail3D(
          'three.js did not load (' + (why || 'unknown') + '). Resolved URL: ' +
          (self.threeUrl || '(EMPTY - is three.global.min.js in the theme assets folder?)')
        );
      }
      self.THREE = THREE;
      try {
        self.build3D();
      } catch (err) {
        console.error('[fieldcenter-designer] 3D scene failed to build', err);
        self.fail3D('3D scene failed to build: ' + (err && err.message));
      }
    });
  };

  /** Visible, explained degradation -- never a silently missing feature. */
  FieldcenterDesigner.prototype.fail3D = function (detail) {
    console.error('[fieldcenter-designer] ' + detail);
    this.root.classList.add('fc--3d-off');
    var stage = this.el.stage3d;
    if (stage) {
      stage.textContent = '';
      var p = document.createElement('p');
      p.className = 'fc-3d-error';
      p.textContent = '3D preview is not available in this browser. Your design ' +
        'and the placement above are still saved with your order.';
      stage.appendChild(p);
    }
    var spin = this.el.spinBtn;
    if (spin) spin.hidden = true;
  };

  FieldcenterDesigner.prototype.build3D = function () {
    var THREE = this.THREE;
    var stage = this.el.stage3d;
    if (!stage) return;

    var renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    // ACES keeps metallic highlights from blowing out, but it also darkens
    // and desaturates the artwork. Exposure is lifted here and the print
    // carries an emissiveMap (see buildCardMeshes) so the customer's colours
    // survive the lighting pipeline rather than being dimmed by it.
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    // 1.10 here (sleeve uses 1.04): the metallic front face reflects more
    // and diffuses less, so the print needs the extra exposure to match the
    // brightness of the flat editor (Mert 2026-09-29: preview read darker
    // than the uploaded art).
    renderer.toneMappingExposure = 1.10;
    stage.appendChild(renderer.domElement);
    this.renderer = renderer;

    var scene = new THREE.Scene();
    this.scene = scene;

    // near/far are deliberately tight around the card. The printed face sits
    // 0.0015mm off the slab it decorates, and a 1..2000 depth range does not
    // have the precision to keep them apart. Near is 40 here rather than the
    // sleeve's 80: this card is smaller, so its fitted camera distance is
    // ~165mm and max zoom (2.6x) dollies to ~63mm — inside an 80mm near plane.
    var camera = new THREE.PerspectiveCamera(28, 1, 40, 700);
    camera.position.set(0, 0, 330);
    this.camera = camera;

    // Procedural studio environment: a vertical gradient equirect, prefiltered.
    // Gives the metal something real to reflect without shipping an HDR.
    var envCanvas = document.createElement('canvas');
    envCanvas.width = 32; envCanvas.height = 128;
    var ectx = envCanvas.getContext('2d');
    var grad = ectx.createLinearGradient(0, 0, 0, 128);
    grad.addColorStop(0.00, '#ffffff');
    grad.addColorStop(0.40, '#dbe3ee');
    grad.addColorStop(0.52, '#7f8798');
    grad.addColorStop(1.00, '#20242c');
    ectx.fillStyle = grad; ectx.fillRect(0, 0, 32, 128);
    var envTex = new THREE.CanvasTexture(envCanvas);
    envTex.mapping = THREE.EquirectangularReflectionMapping;
    var pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromEquirectangular(envTex).texture;
    envTex.dispose();

    // A second environment just for the faces: a bright softbox band on a
    // dark studio, so the metal carries a visible light reflection that
    // sweeps across the surface as the card rotates.
    var glossCanvas = document.createElement('canvas');
    glossCanvas.width = 256; glossCanvas.height = 128;
    var gctx = glossCanvas.getContext('2d');
    gctx.fillStyle = '#23262e';
    gctx.fillRect(0, 0, 256, 128);
    var band = gctx.createLinearGradient(0, 20, 0, 78);
    band.addColorStop(0, 'rgba(255,255,255,0)');
    band.addColorStop(0.5, 'rgba(255,255,255,0.95)');
    band.addColorStop(1, 'rgba(255,255,255,0)');
    gctx.fillStyle = band;
    gctx.save();
    gctx.translate(128, 48); gctx.rotate(-0.18);
    gctx.fillRect(-140, -30, 280, 60);
    gctx.restore();
    var glossTex = new THREE.CanvasTexture(glossCanvas);
    glossTex.mapping = THREE.EquirectangularReflectionMapping;
    this.envGloss = pmrem.fromEquirectangular(glossTex).texture;
    glossTex.dispose();
    pmrem.dispose();

    scene.add(new THREE.HemisphereLight(0xffffff, 0x404450, 0.55));
    var key = new THREE.DirectionalLight(0xffffff, 2.0);
    key.position.set(-120, 180, 240); scene.add(key);
    var fill = new THREE.DirectionalLight(0xdfe8ff, 0.7);
    fill.position.set(180, -60, 160); scene.add(fill);
    var rim = new THREE.DirectionalLight(0xffffff, 1.1);
    rim.position.set(60, 40, -220); scene.add(rim);

    this.group = new THREE.Group();
    scene.add(this.group);

    this.updateFxCanvas();
    this.tex = new THREE.CanvasTexture(this.fxCanvas);
    this.tex.colorSpace = THREE.SRGBColorSpace;
    this.tex.anisotropy = renderer.capabilities.getMaxAnisotropy();

    this.buildCardMeshes();
    this.applyMetalMaterial();
    this.updateBackTexture();

    // opening pose: printed front toward the viewer, slight tilt
    this.group.rotation.y = -0.26;
    this.group.rotation.x = 0.07;

    this.bindOrbit();
    this.resize3D();
    this.startLoop();
  };

  FieldcenterDesigner.prototype.roundedShape = function (w, h, r) {
    var THREE = this.THREE;
    var s = new THREE.Shape();
    var x = -w / 2, y = -h / 2;
    r = Math.min(r, w / 2, h / 2);

    // With r at 0 the quadratic curves below collapse to zero-length control
    // segments, which extrudes fine but leaves needless vertices -- emit a
    // clean rectangle instead.
    if (!(r > 0.01)) {
      s.moveTo(x, y);
      s.lineTo(x + w, y);
      s.lineTo(x + w, y + h);
      s.lineTo(x, y + h);
      s.lineTo(x, y);
      return s;
    }

    s.moveTo(x + r, y);
    s.lineTo(x + w - r, y);
    s.quadraticCurveTo(x + w, y, x + w, y + r);
    s.lineTo(x + w, y + h - r);
    s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    s.lineTo(x + r, y + h);
    s.quadraticCurveTo(x, y + h, x, y + h - r);
    s.lineTo(x, y + r);
    s.quadraticCurveTo(x, y, x + r, y);
    return s;
  };

  /** UV-maps a flat face geometry so the texture spans the card exactly. */
  FieldcenterDesigner.prototype.faceGeometry = function (shape, w, h) {
    var THREE = this.THREE;
    var geo = new THREE.ShapeGeometry(shape, 16);
    var pos = geo.attributes.position, uv = geo.attributes.uv;
    for (var i = 0; i < pos.count; i++) {
      uv.setXY(i, (pos.getX(i) + w / 2) / w, (pos.getY(i) + h / 2) / h);
    }
    uv.needsUpdate = true;
    return geo;
  };

  FieldcenterDesigner.prototype.buildCardMeshes = function () {
    var THREE = this.THREE;
    var g = this.group;

    while (g.children.length) {
      var c = g.children.pop();
      if (c.geometry) c.geometry.dispose();
    }

    var w = this.cardW, h = this.cardH, r = this.cornerMM;
    var shape = this.roundedShape(w, h, r);

    // ---- real metal construction, in mm --------------------------------
    // One solid gold slab (~0.6mm of metal). The printed front sits just off
    // its +Z face; the fixed embossed gold back just off its -Z face. No
    // bevels: a bevelled extrusion spans depth + 2*bevelThickness rather
    // than depth, which is the exact thing that buried the printed face on
    // the sleeve tool's first build. Rounded corners come from the shape
    // outline instead.
    var D = this.thicknessMM * this.thicknessScale;
    var halfT = D / 2;
    this.cardThickness = D;

    // the slab: gold metal, visible on the edges
    var slabGeo = new THREE.ExtrudeGeometry(shape, {
      depth: D, bevelEnabled: false, curveSegments: 16
    });
    slabGeo.translate(0, 0, -halfT);
    this.slabMat = new THREE.MeshPhysicalMaterial({
      color: 0xc9a545,
      metalness: 0.95, roughness: 0.32,
      clearcoat: 0.6, clearcoatRoughness: 0.2
    });
    g.add(new THREE.Mesh(slabGeo, this.slabMat));

    // the print, on the front (+Z) face. polygonOffset keeps it from
    // z-fighting with the slab it sits on.
    var artGeo = this.faceGeometry(shape, w, h);
    artGeo.translate(0, 0, halfT + 0.0015);
    // emissiveMap adds the artwork's own colour independent of lighting, so
    // the 3D print reads at the same brightness as the flat editor. Without
    // it the card looked noticeably darker than the image the customer
    // uploaded. The metallic response on top is set in applyMetalMaterial.
    this.artMat = new THREE.MeshPhysicalMaterial({
      map: this.tex,
      emissive: 0xffffff,
      emissiveMap: this.tex,
      emissiveIntensity: 0.20,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2
    });
    this.artMesh = new THREE.Mesh(artGeo, this.artMat);
    g.add(this.artMesh);

    // the fixed embossed gold back, on the -Z face. Same geometry, and the
    // MESH is rotated half a turn so the face (and its texture) looks out
    // the back the right way round.
    var backGeo = this.faceGeometry(shape, w, h);
    backGeo.translate(0, 0, halfT + 0.0015);
    this.backMat = new THREE.MeshPhysicalMaterial({
      color: 0xd9b559,
      metalness: 0.9, roughness: 0.35,
      clearcoat: 0.6, clearcoatRoughness: 0.2,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2
    });
    this.backMesh = new THREE.Mesh(backGeo, this.backMat);
    this.backMesh.rotation.y = Math.PI;
    g.add(this.backMesh);

    // kick off the back texture load; updateBackTexture applies it on load
    this.backImage();
  };

  /**
   * The metal response of both faces. One finish only — the card is gold
   * metal — so unlike the sleeve tool there is no per-variant switching;
   * this exists to keep the material recipe in one place.
   */
  FieldcenterDesigner.prototype.applyMetalMaterial = function () {
    if (!this.artMat) return;
    var m = this.artMat;

    // Printed metal: the ink sits on brushed gold, so the face keeps a
    // metallic sheen with a hard clearcoat sweep from the softbox env.
    // Retuned 2026-09-29 (Mert: preview darker/softer than the uploaded
    // art): metalness down from 0.75 — full-metal shading throws away the
    // diffuse response, which under ACES read as a dark, muddy print. Ink
    // on metal is mostly dielectric anyway; the metal look now comes from
    // the clearcoat sweep, the gold slab edges and the back. Emissive up
    // so the artwork holds the flat editor's brightness and contrast.
    m.metalness = 0.45;
    m.roughness = 0.22;
    m.clearcoat = 1.0;
    m.clearcoatRoughness = 0.06;
    m.emissiveIntensity = 0.32;
    if (this.envGloss) {
      m.envMap = this.envGloss;
      m.envMapIntensity = 1.0;
    }
    m.needsUpdate = true;

    if (this.backMat) {
      if (this.envGloss) {
        this.backMat.envMap = this.envGloss;
        this.backMat.envMapIntensity = 1.0;
      }
      this.backMat.needsUpdate = true;
    }
    if (this.slabMat) this.slabMat.needsUpdate = true;
    this.needsRender = true;
  };

  /** Applies the embossed gold back photo to the back face once loaded. */
  FieldcenterDesigner.prototype.updateBackTexture = function () {
    if (!this.THREE || !this.backMat) return;
    var img = this.backImage();
    if (!img) return;
    if (!this.backTex) {
      this.backTex = new this.THREE.Texture(img);
      this.backTex.colorSpace = this.THREE.SRGBColorSpace;
      if (this.renderer) {
        this.backTex.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
      }
      this.backTex.needsUpdate = true;
      this.backMat.map = this.backTex;
      this.backMat.color.set(0xffffff);
      // same brightness-survival trick as the front
      this.backMat.emissive = new this.THREE.Color(0xffffff);
      this.backMat.emissiveMap = this.backTex;
      this.backMat.emissiveIntensity = 0.18;
      this.backMat.needsUpdate = true;
    }
    this.needsRender = true;
  };

  FieldcenterDesigner.prototype.bindOrbit = function () {
    var self = this;
    var dom = this.renderer.domElement;
    var drag = false, last = null;
    var pointers = {};                 // active pointers, for pinch zoom
    var pinchDist = null;
    dom.style.touchAction = 'none';

    function pointerCount() { return Object.keys(pointers).length; }
    function pinchDistance() {
      var ids = Object.keys(pointers);
      var a = pointers[ids[0]], b = pointers[ids[1]];
      return Math.hypot(a.x - b.x, a.y - b.y);
    }

    dom.addEventListener('pointerdown', function (e) {
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      drag = pointerCount() === 1;
      if (pointerCount() === 2) { drag = false; pinchDist = pinchDistance(); }
      last = { x: e.clientX, y: e.clientY };
      self.spin = false;
      dom.setPointerCapture(e.pointerId);
    });
    dom.addEventListener('pointermove', function (e) {
      if (pointers[e.pointerId]) pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      if (pointerCount() === 2 && pinchDist) {
        var nd = pinchDistance();
        if (nd > 0) { self.nudgeZoom3D(nd / pinchDist); pinchDist = nd; }
        return;
      }
      if (!drag) return;
      var dx = e.clientX - last.x, dy = e.clientY - last.y;
      self.group.rotation.y += dx * 0.008;
      self.group.rotation.x = clamp(self.group.rotation.x + dy * 0.006, -0.9, 0.9);
      last = { x: e.clientX, y: e.clientY };
      self.needsRender = true;
    });
    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (evt) {
      dom.addEventListener(evt, function (e) {
        delete pointers[e.pointerId];
        if (pointerCount() < 2) pinchDist = null;
        if (pointerCount() === 0) drag = false;
      });
    });

    // scroll-wheel zoom, like the 2D editor's
    dom.addEventListener('wheel', function (e) {
      e.preventDefault();
      self.nudgeZoom3D(e.deltaY < 0 ? 1.08 : 1 / 1.08);
    }, { passive: false });

    // optional +/- buttons over the stage
    var zin = this.root.querySelector('[data-fc-zoom3d-in]');
    var zout = this.root.querySelector('[data-fc-zoom3d-out]');
    if (zin) zin.addEventListener('click', function () { self.nudgeZoom3D(1.2); });
    if (zout) zout.addEventListener('click', function () { self.nudgeZoom3D(1 / 1.2); });
  };

  FieldcenterDesigner.prototype.toggleSpin = function () {
    this.spin = !this.spin;
    if (this.el.spinBtn) {
      this.el.spinBtn.setAttribute('aria-pressed', this.spin ? 'true' : 'false');
    }
  };

  FieldcenterDesigner.prototype.resize3D = function () {
    if (!this.renderer || !this.el.stage3d) return;
    var w = this.el.stage3d.clientWidth || 320;
    var h = this.el.stage3d.clientHeight || Math.round(w * 1.1);
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    var aspect = w / h;
    this.camera.aspect = aspect;
    // Fit the card to whichever axis is tighter. Visible height at distance d
    // is 2*d*tan(fov/2); visible width is that times the aspect ratio.
    var need = Math.max(this.cardH, this.cardW / aspect) * 1.23;
    this.baseCamZ = need / (2 * Math.tan(this.camera.fov * Math.PI / 360));
    this.applyZoom3D();
    this.camera.updateProjectionMatrix();
    this.needsRender = true;
  };

  /** 3D preview zoom: 1 = fitted, >1 dollies the camera in. */
  FieldcenterDesigner.prototype.applyZoom3D = function () {
    if (!this.camera || !this.baseCamZ) return;
    this.zoom3D = clamp(this.zoom3D || 1, 0.6, 2.6);
    this.camera.position.z = this.baseCamZ / this.zoom3D;
    this.needsRender = true;
  };

  FieldcenterDesigner.prototype.nudgeZoom3D = function (factor) {
    this.zoom3D = clamp((this.zoom3D || 1) * factor, 0.6, 2.6);
    this.applyZoom3D();
  };

  FieldcenterDesigner.prototype.startLoop = function () {
    var self = this;
    this.needsRender = true;
    function frame() {
      if (self.spin) {
        self.group.rotation.y += 0.006;
        self.needsRender = true;
      }
      if (self.needsRender) {
        self.renderer.render(self.scene, self.camera);
        self.needsRender = false;
      }
      self.raf = requestAnimationFrame(frame);
    }
    frame();
  };

  // ------------------------------------------------------- variant plumbing

  FieldcenterDesigner.prototype.findForm = function () {
    // Related products and quick-add drawers render their own /cart/add forms,
    // so an unscoped querySelector can grab the wrong one. Prefer a form inside
    // the main product wrapper.
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    var fallback = null;
    for (var i = 0; i < forms.length; i++) {
      var f = forms[i];
      if (!f.querySelector('[name="id"]')) continue;
      if (!fallback) fallback = f;
      if (f.closest('.main-product-view, #MainProduct, [data-section-type="main-product"]')) {
        return f;
      }
      if (this.root && this.root.contains(f)) return f;
    }
    return fallback;
  };

  /**
   * The element option controls must live inside for Shopify to honour them.
   * A page can hold several sets of identically-named option radios: the main
   * product, a quick-view drawer, a featured-product section. Querying the
   * whole document produced duplicate pills on the other tools — and worse,
   * proxying a click to a radio outside <product-info> means product-info.js
   * discards it and the pill does nothing at all.
   */
  FieldcenterDesigner.prototype.productScope = function () {
    var form = this.findForm();
    var scope = form && form.closest
      ? form.closest('product-info, .main-product-view, #MainProduct')
      : null;
    if (!scope) {
      scope = document.querySelector('product-info') ||
              document.querySelector('.main-product-view, #MainProduct');
    }
    return scope || document;
  };

  /**
   * Finds the REAL controls for one product option. Metro names them
   * differently depending on picker type, which is a trap:
   *   dropdown / select picker -> name="options[Field Center Design]"
   *   button / swatch picker   -> name="Field Center Design"
   */
  FieldcenterDesigner.prototype.realControlsFor = function (name) {
    var esc = String(name).replace(/"/g, '\\"');
    var sel = 'input[type="radio"][name="' + esc + '"],' +
              'input[type="radio"][name="options[' + esc + ']"],' +
              'select[name="options[' + esc + ']"],' +
              'select[name="' + esc + '"]';
    return this.productScope().querySelectorAll(sel);
  };

  /** Current option values + availability, read from the real controls. */
  FieldcenterDesigner.prototype.readRealOptions = function () {
    var names = this.optionNames || [];
    var out = [];
    for (var i = 0; i < names.length; i++) {
      var nodes = this.realControlsFor(names[i]);
      if (!nodes.length) continue;
      var group = { name: names[i], values: [], selected: null };
      var seen = {};                      // one entry per distinct value
      function add(value, checked, disabled) {
        var key = '@' + value;
        if (seen[key]) {
          // merge: selected anywhere wins, unavailable everywhere to disable
          if (checked) { seen[key].checked = true; group.selected = value; }
          if (!disabled) seen[key].disabled = false;
          return;
        }
        seen[key] = { value: value, checked: !!checked, disabled: !!disabled };
        group.values.push(seen[key]);
        if (checked) group.selected = value;
      }
      for (var j = 0; j < nodes.length; j++) {
        var n = nodes[j];
        if (n.tagName === 'SELECT') {
          for (var k = 0; k < n.options.length; k++) {
            var o = n.options[k];
            add(o.value, o.selected, o.disabled);
          }
        } else {
          var dis = n.disabled || n.classList.contains('variant-input-disabled') ||
                    (n.parentNode && n.parentNode.classList &&
                     n.parentNode.classList.contains('variant-disabled'));
          add(n.value, n.checked, dis);
        }
      }
      out.push(group);
    }
    return out;
  };

  FieldcenterDesigner.prototype.syncFromVariantPicker = function () {
    var groups = this.readRealOptions();
    var vals = {};
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].selected != null) vals[groups[i].name] = groups[i].selected;
    }
    this.applyOptionValues(vals);
    return vals;
  };

  FieldcenterDesigner.prototype.applyOptionValues = function (vals) {
    var changed = false;
    var markVal = vals[this.markingsOptionName];
    if (markVal !== undefined && markVal !== this.state.markValue) {
      this.state.markValue = markVal; changed = true;
    }
    if (changed) {
      this.render2D();
      this.syncMarkColorRow();
    }
    return changed;
  };

  FieldcenterDesigner.prototype.applyVariant = function (variant) {
    if (!variant || !variant.options) return this.syncFromVariantPicker();
    var vals = {};
    // variant.options is positional; map via the picker's option names
    var names = this.optionNames || [];
    for (var i = 0; i < variant.options.length; i++) {
      if (names[i]) vals[names[i]] = variant.options[i];
    }
    if (!Object.keys(vals).length) return this.syncFromVariantPicker();
    this.applyOptionValues(vals);
  };

  FieldcenterDesigner.prototype.watchVariant = function () {
    var self = this;
    this.optionNames = parseJSONAttr(this.root, 'data-option-names', []);

    function afterChange() {
      self.ensureFormInputs(); self.wireAtc(); self.updateGate();
      self.hideOriginalPicker(); self.adoptBuyButtons(); self.adoptQuantity(); self.adoptPaymentTerms();
      self.alignPriceToButtons();
      self.refreshOptionProxy(); self.updatePrice();
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        subscribe(PUB_SUB_EVENTS.variantChange, function (evt) {
          var v = evt && evt.data && evt.data.variant;
          self.applyVariant(v);
          // the form is re-rendered on variant change; re-inject and re-gate
          setTimeout(afterChange, 0);
        });
      } catch (e) { /* fall through to DOM listeners */ }
    }

    document.addEventListener('variant:change', function (e) {
      self.applyVariant(e.detail && e.detail.variant);
      setTimeout(afterChange, 0);
    });

    // belt and braces: any change in the picker
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.name) return;
      var names = self.optionNames || [];
      var hit = t.name.indexOf('options[') === 0;
      for (var i = 0; i < names.length && !hit; i++) {
        if (t.name === names[i]) hit = true;
      }
      if (!hit) return;
      self.syncFromVariantPicker();
      setTimeout(afterChange, 60);
    });
  };

  // ------------------------------------------------- option proxy + pricing

  /**
   * Shortens an option value for a pill label. Only safe because these pills
   * are a PROXY: the real radio inside <product-info> keeps the full value,
   * so cart and order data are untouched.
   */
  FieldcenterDesigner.prototype.shortLabel = function (value) {
    if (!this.shortenLabels) return value;
    var v = String(value);
    v = v.replace(/\s*\([^)]*\)/g, '');
    v = v.replace(/\s*field\s*center\s*/i, ' ');
    v = v.replace(/\s{2,}/g, ' ').trim();
    return v || String(value);
  };

  /**
   * Renders proxy pills for every product option (this product has one:
   * Field Center Design), plus the markings-colour row beneath, into the
   * host beside the upload window. Must be a PROXY: product-info.js ignores
   * option changes whose event target is outside <product-info>, so the
   * pills CLICK the real hidden inputs.
   */
  FieldcenterDesigner.prototype.buildOptionProxy = function () {
    var host = this.el.options;
    if (!host) return;

    var groups = this.readRealOptions();
    host.textContent = '';
    host.hidden = true;
    if (!groups.length) return;

    var self = this;
    groups.forEach(function (g) {
      var row = document.createElement('div');
      row.className = 'fc-opt';
      row.setAttribute('data-fc-opt-row', g.name);

      var lab = document.createElement('span');
      lab.className = 'fc-opt-label';
      lab.textContent = g.name;
      row.appendChild(lab);

      var ul = document.createElement('ul');
      ul.className = 'swatches-list';

      g.values.forEach(function (v) {
        var li = document.createElement('li');
        li.className = 'swatches-list-item' +
          (v.checked ? ' active' : '') + (v.disabled ? ' variant-disabled' : '');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'swatch-label';
        btn.textContent = self.shortLabel(v.value);
        btn.title = v.value;
        btn.setAttribute('data-fc-opt', g.name);
        btn.setAttribute('data-fc-val', v.value);
        if (v.disabled) btn.setAttribute('aria-disabled', 'true');
        li.appendChild(btn);
        ul.appendChild(li);
      });

      row.appendChild(ul);
      host.appendChild(row);
    });

    // markings colour row, under the option pills
    host.appendChild(this.buildMarkColorRow());
    host.hidden = false;

    // one delegated listener on the root
    if (!this.root.__fcOptBound) {
      this.root.__fcOptBound = true;
      this.root.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('[data-fc-val]') : null;
        if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
        self.chooseOption(btn.getAttribute('data-fc-opt'), btn.getAttribute('data-fc-val'));
      });
    }

    this.refreshOptionProxy();
  };

  /** White / Black swatches for the markings colour. Hidden while the
   *  "No ... Markings" variant is selected — it means nothing then. */
  FieldcenterDesigner.prototype.buildMarkColorRow = function () {
    var self = this;
    var row = document.createElement('div');
    row.className = 'fc-mark-colors';
    row.setAttribute('data-fc-mark-colors', '');
    row.hidden = true;
    var lab = document.createElement('span');
    lab.className = 'fc-opt-label';
    lab.textContent = this.markColorLabel;
    row.appendChild(lab);
    var wrap = document.createElement('div');
    wrap.className = 'fc-swatches';
    row.appendChild(wrap);
    this.el.markRow = row;
    this.el.markWrap = wrap;
    this.renderMarkSwatches();
    return row;
  };

  /**
   * (Re)fills the swatch strip: White, Black, then every picked custom
   * colour as its own swatch, with the rainbow picker always LAST
   * (2026-09-29, Mert: picks accumulate instead of replacing the picker).
   * Up to 5 custom swatches; a sixth pick replaces the oldest.
   */
  FieldcenterDesigner.prototype.renderMarkSwatches = function () {
    var self = this;
    var wrap = this.el.markWrap;
    if (!wrap) return;
    wrap.textContent = '';

    function addSwatch(key, bg, label) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'fc-swatch';
      btn.setAttribute('data-fc-mark-color', key);
      btn.setAttribute('aria-label', label);
      btn.style.background = bg;
      btn.addEventListener('click', function () { self.setMarkColor(key); });
      wrap.appendChild(btn);
      return btn;
    }

    addSwatch('white', '#f5f5f2', this.markColorLabel + ' white');
    addSwatch('black', '#101013', this.markColorLabel + ' black');
    for (var i = 0; i < this.state.customColors.length; i++) {
      var hex = this.state.customColors[i];
      addSwatch(hex, hex, this.markColorLabel + ' ' + hex);
    }

    // The picker itself, always at the end of the strip. A native
    // <input type="color"> does the actual picking; it sits visually hidden
    // inside the row so the browser anchors the picker beside the swatch.
    // While the customer drags inside the picker the preview follows live
    // ('input'); only closing/committing the picker ('change') turns the
    // colour into a swatch — otherwise one drag would mint dozens.
    var pick = document.createElement('button');
    pick.type = 'button';
    pick.className = 'fc-swatch fc-swatch--picker';
    pick.setAttribute('data-fc-mark-picker', '');
    pick.setAttribute('aria-label', this.markColorLabel + ' — pick any colour');
    pick.title = 'Pick any colour';
    var cinput = document.createElement('input');
    cinput.type = 'color';
    cinput.value = this.state.markColor.charAt(0) === '#'
      ? this.state.markColor
      : (this.state.customColors[this.state.customColors.length - 1] || '#ffd700');
    cinput.className = 'fc-visually-hidden';
    cinput.setAttribute('data-fc-mark-color-input', '');
    cinput.tabIndex = -1;
    pick.addEventListener('click', function () {
      try { cinput.click(); } catch (e) { /* picker unsupported */ }
    });
    cinput.addEventListener('input', function () {
      self.previewCustomColor(this.value);
    });
    cinput.addEventListener('change', function () {
      self.commitCustomColor(this.value);
    });
    wrap.appendChild(pick);
    wrap.appendChild(cinput);

    this.syncMarkColorRow();
  };

  FieldcenterDesigner.prototype.setMarkColor = function (key) {
    var isHex = /^#[0-9a-f]{6}$/i.test(String(key));
    if (key !== 'white' && key !== 'black' && !isHex) return;
    if (isHex) key = String(key).toLowerCase();
    if (this.state.markColor === key) return;
    this.state.markColor = key;
    this.render2D();
    this.syncMarkColorRow();
    this.ensureFormInputs();
  };

  /** Live preview while dragging inside the native picker: the markings
   *  re-tint but no swatch is created yet. */
  FieldcenterDesigner.prototype.previewCustomColor = function (hex) {
    if (!/^#[0-9a-f]{6}$/i.test(String(hex))) return;
    this.state.markColor = String(hex).toLowerCase();
    this.render2D();
    this.syncMarkColorRow();
    this.ensureFormInputs();
  };

  /** Closing the picker commits the colour as its own swatch, after Black
   *  (and any earlier picks). Max 5 custom swatches; the oldest is
   *  replaced once the strip is full. Re-picking an existing swatch's
   *  colour just selects it. */
  FieldcenterDesigner.prototype.commitCustomColor = function (hex) {
    if (!/^#[0-9a-f]{6}$/i.test(String(hex))) return;
    hex = String(hex).toLowerCase();
    var list = this.state.customColors;
    if (list.indexOf(hex) === -1) {
      list.push(hex);
      if (list.length > 5) list.shift();
    }
    this.state.markColor = hex;
    this.renderMarkSwatches();
    this.render2D();
    this.ensureFormInputs();
  };

  FieldcenterDesigner.prototype.syncMarkColorRow = function () {
    var row = this.el.markRow || this.root.querySelector('[data-fc-mark-colors]');
    if (!row) return;
    row.hidden = !this.markingsOn();
    var btns = row.querySelectorAll('[data-fc-mark-color]');
    for (var i = 0; i < btns.length; i++) {
      var key = btns[i].getAttribute('data-fc-mark-color');
      var on = key === this.state.markColor;
      btns[i].classList.toggle('fc-swatch--on', on);
      btns[i].setAttribute('aria-pressed', on ? 'true' : 'false');
    }
    // a previewed (not yet committed) colour highlights the picker itself
    var pick = row.querySelector('[data-fc-mark-picker]');
    if (pick) {
      var previewing = this.state.markColor.charAt(0) === '#' &&
        this.state.customColors.indexOf(this.state.markColor) === -1;
      pick.classList.toggle('fc-swatch--on', previewing);
    }
    var form = this.form || this.findForm();
    var prop = form && form.querySelector('[data-fc-prop="markcol"]');
    if (prop) {
      var val = this.markColorProperty();
      prop.value = val;
      prop.disabled = !val;   // a disabled input never submits an empty property
    }
  };

  /** The order property: only meaningful while markings print. A custom
   *  colour is recorded as its hex code, e.g. "#FF6600". */
  FieldcenterDesigner.prototype.markColorProperty = function () {
    if (!this.markingsOn()) return '';
    if (this.state.markColor.charAt(0) === '#') return this.state.markColor.toUpperCase();
    return this.state.markColor === 'black' ? 'Black' : 'White';
  };

  /**
   * Forwards a pill click to the real control. product-info.js does
   * `if (!this.contains(event.target)) return;` — it ignores any option change
   * whose event target is outside <product-info>, so the proxy CLICKS the real
   * input rather than re-creating the picker somewhere Metro won't listen.
   */
  FieldcenterDesigner.prototype.chooseOption = function (name, value) {
    var nodes = this.realControlsFor(name);
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.tagName === 'SELECT') {
        if (n.value === value) return;
        n.value = value;
        n.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      if (n.value === value) {
        if (n.checked) return;
        n.click();                                  // fires change from the real input
        return;
      }
    }
  };

  /** Mirrors selected/disabled state back onto the pills. */
  FieldcenterDesigner.prototype.refreshOptionProxy = function () {
    var root = this.root;
    var groups = this.readRealOptions();
    groups.forEach(function (g) {
      var row = root.querySelector('[data-fc-opt-row="' + String(g.name).replace(/"/g, '\\"') + '"]');
      if (!row) return;
      g.values.forEach(function (v) {
        var btn = row.querySelector('[data-fc-val="' + String(v.value).replace(/"/g, '\\"') + '"]');
        if (!btn) return;
        var li = btn.parentNode;
        li.classList.toggle('active', !!v.checked);
        li.classList.toggle('variant-disabled', !!v.disabled);
        if (v.disabled) btn.setAttribute('aria-disabled', 'true');
        else btn.removeAttribute('aria-disabled');
      });
    });
    this.syncMarkColorRow();
  };

  /** Hides Metro's own picker so the page shows one set of options, not two.
   *  Visually hidden only — these inputs are what the pills click, and they
   *  must stay in the DOM, inside <product-info>, or selection breaks. */
  FieldcenterDesigner.prototype.hideOriginalPicker = function () {
    if (!this.hideOriginal) return;
    var scope = this.productScope();
    if (!scope || !scope.querySelectorAll) return;
    var nodes = scope.querySelectorAll('variant-selects, .product-block-variant-picker');
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].classList.contains('fc-visually-hidden')) {
        nodes[i].classList.add('fc-visually-hidden');
      }
    }
  };

  // ---- pricing -----------------------------------------------------------

  FieldcenterDesigner.prototype.moneyParts = function () {
    if (this._money) return this._money;
    var m = String(this.moneySample);
    var first = m.search(/[0-9]/);
    var last = m.length - 1 - String(m).split('').reverse().join('').search(/[0-9]/);
    this._money = {
      pre: first > 0 ? m.slice(0, first) : '',
      post: last < m.length - 1 ? m.slice(last + 1) : '',
      comma: /1[.,]00/.test(m) ? m.charAt(m.search(/1[.,]00/) + 1) : '.'
    };
    return this._money;
  };

  FieldcenterDesigner.prototype.formatCents = function (cents) {
    var mp = this.moneyParts();
    var whole = Math.floor(cents / 100);
    var frac = String(Math.round(cents % 100));
    while (frac.length < 2) frac = '0' + frac;
    var s = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, mp.comma === ',' ? '.' : ',');
    return mp.pre + s + mp.comma + frac + mp.post;
  };

  FieldcenterDesigner.prototype.currentVariant = function () {
    var names = this.optionNames || [];
    var groups = this.readRealOptions();
    var sel = {};
    groups.forEach(function (g) { if (g.selected != null) sel[g.name] = g.selected; });
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i], ok = true;
      for (var j = 0; j < names.length; j++) {
        if (sel[names[j]] !== undefined && v.o[j] !== sel[names[j]]) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  };

  FieldcenterDesigner.prototype.updatePrice = function () {
    if (!this.el.priceNow) return;
    var v = this.currentVariant();
    if (!v) { this.el.priceNow.textContent = ''; return; }

    this.el.priceNow.textContent = v.p;

    if (this.el.priceWas) {
      var show = v.cp && v.cp > v.c;
      this.el.priceWas.textContent = show ? this.formatCents(v.cp) : '';
      this.el.priceWas.hidden = !show;
    }
  };

  // ---- add to cart -------------------------------------------------------

  /**
   * Associates our button with the real product form via the form="" attribute,
   * so it submits through product-form.js exactly like Metro's own button --
   * including the AJAX cart drawer and our own artwork gate.
   */
  FieldcenterDesigner.prototype.wireAtc = function () {
    var btn = this.el.atc;
    if (!btn) return;
    var form = this.findForm();
    if (!form) return;

    // NOT form.id -- every Shopify product form contains <input name="id"> for
    // the variant, and HTMLFormElement's legacy named-property getter exposes
    // that input as an OWN property, shadowing the inherited `id` accessor.
    // Always go through get/setAttribute on a product form.
    var formId = form.getAttribute('id');
    if (!formId) {
      formId = 'FieldcenterDesignerProductForm';
      form.setAttribute('id', formId);
    }
    btn.setAttribute('form', formId);

    if (btn.__fcWired) return;
    btn.__fcWired = true;
    btn.addEventListener('click', function () {
      if (btn.getAttribute('aria-disabled') === 'true') return;
      btn.classList.add('fc-loading');
      setTimeout(function () { btn.classList.remove('fc-loading'); }, 5000);
    });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () { btn.classList.remove('fc-loading'); });
        subscribe(PUB_SUB_EVENTS.cartError, function () { btn.classList.remove('fc-loading'); });
      } catch (e) { /* not fatal */ }
    }
  };

  /**
   * Relocates Metro's own buy-buttons block into the tool. Unlike the variant
   * picker, this CAN be moved: every `this.productForm` reference in
   * product-info.js is null-safe, and the variant re-render finds
   * `#ProductBuyButtons-…` by id across the whole document. The containment
   * guard only governs option changes, not form submission.
   */
  FieldcenterDesigner.prototype.adoptBuyButtons = function () {
    if (!this.adoptBuy) return;
    var slot = this.el.buySlot;
    if (!slot) return;
    var scope = this.productScope();
    var bb = (scope.querySelector && scope.querySelector('[id^="ProductBuyButtons-"]')) ||
             document.querySelector('[id^="ProductBuyButtons-"]');
    if (!bb || slot.contains(bb)) return;
    slot.appendChild(bb);
    this.root.classList.add('fc--adopted-buy');
  };

  /**
   * Relocates Metro's quantity stepper into the tool, under the markings
   * colour row (2026-09-29, Mert). Safe to move like the buy buttons: the
   * quantity input is associated with the product form via its form=""
   * attribute, so it keeps submitting from its new home, and the
   * <quantity-set> element carries its own +/- listeners with it.
   */
  FieldcenterDesigner.prototype.adoptQuantity = function () {
    if (!this.moveQuantity) return;
    var slot = this.el.qtySlot;
    if (!slot) return;
    var scope = this.productScope();
    var holder = (scope.querySelector && scope.querySelector('.product-block-quantity-holder')) ||
                 document.querySelector('.product-block-quantity-holder');
    if (!holder || slot.contains(holder)) return;
    slot.appendChild(holder);
    this.root.classList.add('fc--adopted-qty');
  };

  /**
   * Turns a run of the product description into one of Metro's collapsible
   * rows. The description is product data shared with other channels, so it
   * is NOT edited: the nodes are relocated in the DOM on this template only.
   */
  FieldcenterDesigner.prototype.collapseDescriptionSection = function () {
    var headingText = (this.collapseHeading || '').trim();
    if (!headingText) return;

    var desc = document.querySelector('[product-description], .product-block-desc');
    if (!desc || desc.__fcCollapsed) return;

    var needle = headingText.toLowerCase().replace(/[:\s]+$/, '');
    var candidates = desc.querySelectorAll('p, h2, h3, h4, h5, h6');
    var head = null;
    for (var i = 0; i < candidates.length; i++) {
      var txt = (candidates[i].textContent || '').trim().toLowerCase().replace(/[:\s]+$/, '');
      if (txt === needle) { head = candidates[i]; break; }
    }
    if (!head) return;

    // everything up to the next paragraph or heading belongs to this section
    var body = [];
    var n = head.nextElementSibling;
    while (n && !/^(P|H[1-6])$/.test(n.tagName)) {
      body.push(n);
      n = n.nextElementSibling;
    }
    if (!body.length) return;

    var wrap = document.createElement('div');
    wrap.className = 'product-block product-collapsible fc-collapsed-section';

    var details;
    try {
      // upgrades to Metro's accordion behaviour where customized built-ins are
      // supported; falls back to a plain <details>, which still opens and closes
      details = document.createElement('details', { is: 'accordion-details' });
    } catch (e) {
      details = document.createElement('details');
    }
    details.className = 'collapsible-product-content-item';
    if (this.collapseOpen) details.setAttribute('open', '');

    var summary = document.createElement('summary');
    summary.className = 'collapsible-heading';
    var label = document.createElement('div');
    label.className = 'collapsible-heading-text text font-bold';
    label.textContent = headingText.replace(/[:\s]+$/, '');
    summary.appendChild(label);

    var icon = document.querySelector('.product-collapsible .collapsible-icon');
    if (icon) {
      summary.appendChild(icon.cloneNode(true));
    } else {
      var span = document.createElement('span');
      span.className = 'collapsible-icon';
      span.textContent = '+';
      summary.appendChild(span);
    }
    details.appendChild(summary);

    var content = document.createElement('div');
    content.className = 'collapsible-content';
    content.setAttribute('collapsible-content-wrapper', '');
    var inner = document.createElement('div');
    inner.className = 'collapsible-content-body rte';
    for (var j = 0; j < body.length; j++) inner.appendChild(body[j]);
    content.appendChild(inner);
    details.appendChild(content);
    wrap.appendChild(details);

    head.parentNode.insertBefore(wrap, head);
    head.parentNode.removeChild(head);
    desc.__fcCollapsed = true;
  };

  /**
   * Moves the Shop Pay installments banner in beneath the express checkout
   * button and above "More payment options". Shopify renders those
   * asynchronously, so this watches for them rather than racing them.
   */
  FieldcenterDesigner.prototype.adoptPaymentTerms = function () {
    if (!this.movePaymentTerms) return;
    var slot = this.el.buySlot;
    if (!slot) return;

    // Hold the node across re-renders: Metro replaces ProductBuyButtons'
    // innerHTML on every variant change, which detaches anything placed inside
    // it. The reference survives, so it can simply be re-inserted.
    var terms = this._paymentTerms ||
                document.querySelector('.product-block-payment-installments');
    if (!terms) return;
    this._paymentTerms = terms;

    if (this._termsPoll) { clearInterval(this._termsPoll); this._termsPoll = null; }
    if (!slot.contains(terms)) slot.appendChild(terms);

    function refine() {
      var payBtn = slot.querySelector('.shopify-payment-button');
      if (!payBtn) return false;
      var more = payBtn.querySelector('.shopify-payment-button__more-options');
      if (more) {
        // the requested spot: under Buy with Shop, above More payment options
        if (terms.parentNode !== payBtn || terms.nextElementSibling !== more) {
          payBtn.insertBefore(terms, more);
        }
        return true;
      }
      // express button present but no more-options link: sit just beneath it
      if (terms.previousElementSibling !== payBtn) {
        payBtn.parentNode.insertBefore(terms, payBtn.nextSibling);
      }
      return false;
    }

    if (refine()) return;
    var self = this;
    var tries = 0;
    this._termsPoll = setInterval(function () {
      tries += 1;
      if (refine() || tries > 40) {                   // ~10s
        clearInterval(self._termsPoll);
        self._termsPoll = null;
      }
    }, 250);
  };

  /**
   * Catches any buy attempt made before a design exists. Bound on .fc-buy in
   * capture phase: the overlay covers the whole buy area, so this receives
   * the click no matter which control was aimed at — including Shopify's
   * express checkout, which may live in an iframe.
   */
  FieldcenterDesigner.prototype.bindBuyGuard = function () {
    var buy = this.root.querySelector('.fc-buy');
    if (!buy || buy.__fcBuyGuard) return;
    buy.__fcBuyGuard = true;
    var self = this;

    buy.addEventListener('click', function (e) {
      if (!self.requireArtwork || self.state.file) return;   // all good, let it through
      e.preventDefault();
      e.stopPropagation();

      self.say('Please upload your design first — both Add to cart and express checkout need it.', true);

      var zone = self.el.dropzone;
      if (zone) {
        zone.classList.remove('fc-flash');
        void zone.offsetWidth;                                // restart the animation
        zone.classList.add('fc-flash');
        setTimeout(function () { zone.classList.remove('fc-flash'); }, 1200);
      }
      var target = self.el.canvasWrap || zone || self.el.view;
      if (target && target.scrollIntoView) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, true);
  };

  /**
   * Pins the price to the Add-to-cart button's actual vertical position.
   * Metro's adopted buy-buttons block renders extra elements above the
   * buttons, so they sit lower than the grid row top while the price stays
   * at it — a gap no static CSS can predict.
   */
  FieldcenterDesigner.prototype.alignPriceToButtons = function () {
    var price = this.root.querySelector('.fc-price');
    var slot = this.el.buySlot;
    if (!price) return;
    var btn = (slot && slot.querySelector(
      '.addtocart-button, .shopify-payment-button__button, [name="add"]'));
    if (!btn || !slot) { price.style.marginTop = ''; return; }
    var offset = Math.round(btn.getBoundingClientRect().top -
                            slot.getBoundingClientRect().top);
    price.style.marginTop = (offset > 0 && offset < 200) ? offset + 'px' : '';
  };

  // ----------------------------------------------------------- cart handoff

  FieldcenterDesigner.prototype.designNote = function () {
    var st = this.state;
    var deg = Math.round(st.rot * 180 / Math.PI);
    var parts = [];

    // Shopify replaces the uploaded filename with a hash, so the customer's
    // own name for the file is recorded here. If a file is ever missing from
    // an order — express checkout is the likely case — this is what lets you
    // ask for the right thing by name.
    if (st.file && st.file.name) parts.push(st.file.name);

    parts = parts.concat([
      'Metal field center ' + this.cardW + 'x' + this.cardH + 'mm',
      'markings ' + (this.markingsOn() ? this.state.markColor : 'none'),
      'scale ' + st.scale.toFixed(2),
      'offset ' + st.tx.toFixed(1) + 'mm,' + st.ty.toFixed(1) + 'mm',
      'rotate ' + deg + 'deg'
    ]);
    if (this.lastCover) {
      var pxPerMM = this.capW / this.cardW;
      parts.push('src ' + Math.round((MM_PER_INCH * pxPerMM) / this.lastCover * (this.srcScale || 1)) + 'ppi');
    }
    return parts.join(' | ');
  };

  FieldcenterDesigner.prototype.setFileOnInput = function (input, file) {
    if (!input) return;
    if (!file) { input.value = ''; return; }
    try {
      var dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
    } catch (e) {
      console.warn('[fieldcenter-designer] could not attach file to form input', e);
    }
  };

  /**
   * Injects the line-item-property inputs into the product form. Must be
   * re-run after every variant change: product-info.js replaces the innerHTML
   * of the buy-buttons container, which destroys these inputs.
   */
  FieldcenterDesigner.prototype.ensureFormInputs = function () {
    var form = this.findForm();
    if (!form) return;
    this.form = form;

    var self = this;
    function ensure(key, type, name) {
      var found = form.querySelector('[data-fc-prop="' + key + '"]');
      if (!found) {
        found = document.createElement('input');
        found.type = type;
        found.name = name;
        found.setAttribute('data-fc-prop', key);
        found.className = 'fc-visually-hidden';
        if (type === 'file') found.setAttribute('tabindex', '-1');
        form.appendChild(found);
      }
      return found;
    }

    var art = ensure('art', 'file', 'properties[Artwork]');
    var shot = ensure('shot', 'file', 'properties[Design Preview]');
    var note = ensure('note', 'hidden', 'properties[_design]');
    var markcol = ensure('markcol', 'hidden', 'properties[Field Center Markings]');

    // Two files per line, per the house convention (order-design-previews):
    // the print-scale placement reference + the customer's original art.
    this.setFileOnInput(art, this.state.file);
    this.setFileOnInput(shot, this.shotFile);
    note.value = this.state.file ? this.designNote() : '';
    markcol.value = this.markColorProperty();
    markcol.disabled = !markcol.value;   // a disabled input never submits an empty property

    if (!form.__fcGuard) {
      form.__fcGuard = true;
      form.addEventListener('submit', function (e) {
        if (!self.requireArtwork) return;

        if (!self.state.file) {
          e.preventDefault();
          e.stopImmediatePropagation();
          self.say('Please upload your design before adding to cart.', true);
          self.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

      }, true);
    }
  };

  FieldcenterDesigner.prototype.updateGate = function () {
    if (!this.requireArtwork) return;
    var form = this.form || this.findForm();
    if (!form) return;
    var ready = !!this.state.file;
    // Includes the sticky button, which sits outside the form and is
    // associated via its form="" attribute. HTMLButtonElement.form resolves
    // that for us.
    var all = document.querySelectorAll('[name="add"]');
    var btns = [];
    for (var n = 0; n < all.length; n++) {
      if (all[n].form === form) btns.push(all[n]);
    }
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      if (ready) {
        if (b.getAttribute('data-fc-blocked') === 'true') {
          b.removeAttribute('data-fc-blocked');
          b.removeAttribute('aria-disabled');
          b.classList.remove('fc-blocked');
        }
      } else {
        b.setAttribute('data-fc-blocked', 'true');
        b.setAttribute('aria-disabled', 'true');
        b.classList.add('fc-blocked');
      }
    }
    this.root.classList.toggle('fc--ready', ready);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
